import { default as railsFetch } from 'lib/railsFetch';
import Poller from 'lib/Poller';
import { t, I18n } from 'lib/I18n';

const computePoints = (rewards) => {
  if (!Array.isArray(rewards)) { return 0; }
  else {
    return rewards.reduce((acc, reward) => reward.points ? acc + reward.points : acc, 0);
  }
}

export function buildEvaluation(props) {
  if (props?.evaluationContext?.https_endpoint_supported) { return new HttpsHandsOnEvaluation(props) }
  else { return new LegacyHandsOnEvaluation(props) }
}

export class HandsOnEvaluation {
  mapError(resp) {
    // railsFetch response is sometimes a string, sometimes an object (WHY??!!?)
    let error = null;
    const errorCode = typeof(resp?.railsFetchStatusCode) !== 'undefined' ? resp.railsFetchStatusCode.toString() : null
  
    if (errorCode === "503") { error = t("challenge.assessment.error.unavailable"); }
    else if (resp?.railsFetchError) { error = resp.railsFetchError; }
    else if (typeof(resp) === 'string' || (typeof(resp) === 'object' && resp !== null)) { error = resp; }
    else { error = t('challenge.assessment.error.generic'); }
  
    return {errorCode, error};
  }

  bodyParams(data) { return data; }

  start({data, interval, maxRetries}) {
    return new Promise((resolve, reject) => {
      railsFetch({url: this.baseUrl, method: 'post', data: this.bodyParams(data)})
        .then(
          (resp) => {
            const poller = new Poller({
              createRequest: this.pollingRequest(resp),
              interval,
              maxRetries,
              isComplete: this.isComplete,
              onComplete: (resp) => resolve(this.mapSuccess(resp)),
              onError: (resp) => reject(this.mapError(resp)),
              onTimeout: () => reject({error: t('challenge.assessment.error.unavailable'), errorCode: "ETIMEOUT"})
            });
            poller.start();
          },
          (resp) => reject(this.mapError(resp))
        );
      }
    );
  }  
}

export class HttpsHandsOnEvaluation extends HandsOnEvaluation {
  #unitUid = null;

  constructor({unitUid, trail_id, trailId, journey, trail, ...rest}) {
    super({unitUid, trail_id, trailId, journey, trail, ...rest});
    this.unitUid = unitUid;
    this.trailId = trail_id ?? trailId;
    this.journey = journey;
    this.trail = trail;
  }

  get baseUrl() {
    return `/api/v1/assessment/units/${this.unitUid}/assessment_evaluation`;
  }

  // Https endpoint requires data to be nested in an `input` object. Older clients will not pass data
  // nested in input, so we nest params properly here if they do not match expected schema
  bodyParams(data) {
    const body = 'input' in data ? { ...data } : { input: data };
    if (this.trailId) { body.trail_id = this.trailId; }
    if (this.journey) { body.journey = this.journey; }
    if (this.trail) { body.trail = this.trail; }
    return body;
  }

  pollingRequest({data: {id}}) { return () => railsFetch({url: `${this.baseUrl}/${id}`, method: 'get'}) }

  isComplete({data: {processing}}) { return processing === false }

  mapSuccess({data: {completed, message, rewards, celebrations, modals}}) {
    return {data: {completed, message, rewards, celebrations, modals, ...{points: computePoints(rewards)}}};
  }
}

export class LegacyHandsOnEvaluation extends HandsOnEvaluation {
  #localePath = '';
  #apiName = '';

  constructor({apiName, trail_id, trailId, journey, trail, ...rest}) {
    super({apiName, trail_id, trailId, journey, trail, ...rest});
    this.apiName = apiName;
    this.trailId = trail_id ?? trailId;
    this.journey = journey;
    this.trail = trail;
    this.localePath = I18n.locale == 'en' ? '/' : `/${I18n.locale}/`;
  }

  get baseUrl() {
    return `${this.localePath}check_challenge/${this.apiName}`;
  }

  bodyParams(data) {
    const result = { ...super.bodyParams(data) };
    if (this.trailId) { result.trail_id = this.trailId; }
    if (this.journey) { result.journey = this.journey; }
    if (this.trail) { result.trail = this.trail; }
    if (data.challenge_params) { result.challenge_params = data.challenge_params; }
    return result;
  }

  pollingRequest(_resp) { return () => railsFetch({url: this.baseUrl, method: 'post'}); }

  isComplete({challenge_status}) { return challenge_status !== 'Processing' && challenge_status !== 'Pass' }

  mapSuccess({challenge_status, challenge_response, rewards, celebrations, modals}) {
    return {data: {completed: challenge_status === 'Success', message: challenge_response, rewards, points: rewards.points, celebrations, modals}};
  }
}
